// Reverb (websocket) contract for campaign-audits — mirrors discoveryRealtime.js.
// The Echo instance is already configured in src/util/echo.js (Bearer + X-Tenant
// authorizer); `useEcho` subscribes to the private channel below.

// Build-time flag: realtime only wires up when a Reverb key is present.
export const REVERB_ENABLED = Boolean(process.env.REACT_APP_REVERB_KEY);

// Private channel per audit + the single status event (branch on payload.status/phase).
export const auditChannel = (id) => `campaign-audit.${id}`;
export const AUDIT_STATUS_EVENT = '.status.updated';

// Stop listening once the audit reaches one of these.
export const TERMINAL_STATUSES = new Set(['analyzed', 'failed', 'archived']);
export const isTerminalStatus = (status) => TERMINAL_STATUSES.has(status);

// Homepage cards render the GENERATING state only while an analysis run is in
// progress. `pending` (files still being read) is excluded: an abandoned ingest can
// stay pending indefinitely, and it must not lock the card's menu.
export const GENERATING_STATUSES = new Set(['processing']);
export const isGeneratingStatus = (status) => GENERATING_STATUSES.has(status);

// Cards that keep a live Reverb listener so they update when the BE settles them.
export const LIVE_STATUSES = new Set(['pending', 'processing']);
export const isLiveStatus = (status) => LIVE_STATUSES.has(status);

// Cards that render the skeleton body (icon rows + score placeholder). `pending`
// (ML still reading the sources, normally 10–30 s) keeps it too — the card has no
// extracted fields yet, so without it the card is empty.
export const SKELETON_STATUSES = new Set(['pending', 'processing']);
export const isSkeletonStatus = (status) => SKELETON_STATUSES.has(status);

// A read that hasn't settled after this long never completed (BE: retry fixes it).
export const STALE_PENDING_MS = 2 * 60 * 1000;

// BE timestamp → epoch ms. Zone-less "YYYY-MM-DD HH:mm:ss" strings are read as UTC
// (server time); ISO strings with a Z/offset are honoured as-is. null if unparseable.
export const parseServerTime = (value) => {
  if (!value) return null;
  const str = String(value)
    .trim()
    .replace(' ', 'T');
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(str);
  const time = new Date(hasZone || !str.includes('T') ? str : `${str}Z`).getTime();
  return Number.isNaN(time) ? null : time;
};

// `pendingSince` (epoch ms) comes from the BE updatedAt/createdAt and is reset to
// "now" after an in-session retry. A future value (clock skew) never counts as stale.
export const isStalePending = (audit, now) =>
  audit?.status === 'pending' &&
  audit.pendingSince != null &&
  now - Math.min(audit.pendingSince, now) > STALE_PENDING_MS;
